/**
 * `/admin/health` — HealthMonitor dashboard (Story 6.4 §AC #7).
 *
 * Shows the overall system status pill plus 4 adapter cards (Meta, Calendar,
 * GHL, OpenAI) with status pill + last latency + last error excerpt. The page
 * polls `/admin/health` every 10s via react-query so the operator sees the
 * 60s tick results in near-real-time (worst case 10s of lag in the UI).
 *
 * Admin + manager get a "Forçar check" button that calls `POST /admin/health/
 * run` to trigger an ad-hoc tick (useful after a deploy / token rotation).
 *
 * RBAC: any authenticated role can read the page. The "Forçar check" button
 * is hidden for operator/viewer.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Activity, AlertCircle, CheckCircle2, AlertTriangle, RefreshCw } from 'lucide-react'

import { api } from '@/lib/api'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { useAuthStore } from '@/stores/auth'

type HealthStatus = 'HEALTHY' | 'DEGRADED' | 'CRITICAL'

interface AdapterSnapshot {
  name: 'meta' | 'calendar' | 'ghl' | 'openai'
  status: HealthStatus
  lastCheckAt: string | null
  lastLatencyMs: number | null
  lastError: string | null
  consecutiveFailures: number
}

interface HealthSnapshot {
  overall: HealthStatus
  checkedAt: string | null
  nextCheckInMs: number | null
  adapters: AdapterSnapshot[]
  legacy?: {
    adapters: Record<string, 'ok' | 'down'>
    missingSettings: string[]
  }
}

interface RunResponse {
  ran: boolean
  overall: HealthStatus
  checkedAt: string | null
}

const ADAPTER_LABELS: Record<AdapterSnapshot['name'], string> = {
  meta: 'Meta WhatsApp Cloud API',
  calendar: 'Calendar (Google/GHL)',
  ghl: 'GoHighLevel API',
  openai: 'OpenAI',
}

function statusBadgeVariant(status: HealthStatus): 'success' | 'warning' | 'destructive' {
  if (status === 'HEALTHY') return 'success'
  if (status === 'DEGRADED') return 'warning'
  return 'destructive'
}

function statusIcon(status: HealthStatus): React.ReactElement {
  if (status === 'HEALTHY') return <CheckCircle2 className="h-4 w-4" aria-hidden="true" />
  if (status === 'DEGRADED') return <AlertTriangle className="h-4 w-4" aria-hidden="true" />
  return <AlertCircle className="h-4 w-4" aria-hidden="true" />
}

function formatLatency(ms: number | null): string {
  if (ms === null) return 'N/A'
  if (ms < 1000) return `${Math.round(ms)} ms`
  return `${(ms / 1000).toFixed(2)} s`
}

function formatRelative(iso: string | null): string {
  if (iso === null) return 'aguardando primeiro check'
  const then = new Date(iso).getTime()
  const now = Date.now()
  const deltaSec = Math.max(0, Math.round((now - then) / 1000))
  if (deltaSec < 60) return `há ${deltaSec}s`
  const min = Math.floor(deltaSec / 60)
  return `há ${min}min`
}

/** Page component — default export for App.tsx router. */
export function HealthPage(): React.ReactElement {
  const role = useAuthStore((s) => s.user?.role)
  const canRun = role === 'admin' || role === 'manager'
  const queryClient = useQueryClient()

  const { data, isLoading, isError } = useQuery<HealthSnapshot>({
    queryKey: ['admin-health'],
    queryFn: () => api<HealthSnapshot>('/admin/health'),
    refetchInterval: 10_000,
    staleTime: 5_000,
  })

  const runMutation = useMutation<RunResponse>({
    mutationFn: async () => api<RunResponse>('/admin/health/run', { method: 'POST', body: '{}' }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin-health'] })
    },
  })

  if (isLoading) {
    return (
      <div className="space-y-6" data-testid="health-loading">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Health</h1>
          <p className="text-sm text-muted-foreground">Carregando status…</p>
        </div>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
          {[0, 1, 2, 3].map((i) => (
            <Card key={i} className="h-32 animate-pulse bg-muted/40" />
          ))}
        </div>
      </div>
    )
  }

  if (isError || data === undefined) {
    return (
      <div className="space-y-4">
        <h1 className="text-2xl font-semibold tracking-tight">Health</h1>
        <Card className="border-destructive/40 bg-destructive/10 p-4" data-testid="health-error">
          <p className="text-sm">Falha ao carregar status. Tente novamente em alguns segundos.</p>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Health</h1>
          <p className="text-sm text-muted-foreground">
            Monitoramento das integrações externas (Meta, Calendar, GHL, OpenAI). Atualiza a cada 10s.
          </p>
        </div>
        {canRun ? (
          <Button
            variant="outline"
            size="sm"
            disabled={runMutation.isPending}
            onClick={() => runMutation.mutate()}
            data-testid="health-force-run"
          >
            <RefreshCw
              className={`mr-2 h-4 w-4 ${runMutation.isPending ? 'animate-spin' : ''}`}
              aria-hidden="true"
            />
            {runMutation.isPending ? 'Verificando…' : 'Forçar check agora'}
          </Button>
        ) : null}
      </div>

      {/* Overall status pill */}
      <Card className="p-5" data-testid="health-overall">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <Activity className="h-5 w-5 text-muted-foreground" aria-hidden="true" />
            <div>
              <div className="text-sm text-muted-foreground">Status geral</div>
              <div className="flex items-center gap-2">
                <Badge variant={statusBadgeVariant(data.overall)} data-testid="health-overall-badge">
                  <span className="mr-1 inline-flex">{statusIcon(data.overall)}</span>
                  {data.overall}
                </Badge>
                <span className="text-xs text-muted-foreground" data-testid="health-checked-at">
                  último check: {formatRelative(data.checkedAt)}
                </span>
              </div>
            </div>
          </div>
          {data.nextCheckInMs !== null ? (
            <div className="text-xs text-muted-foreground" data-testid="health-next-check">
              próximo check em {Math.max(0, Math.round(data.nextCheckInMs / 1000))}s
            </div>
          ) : null}
        </div>
      </Card>

      {/* 4 adapter cards */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
        {data.adapters.map((adapter) => (
          <Card key={adapter.name} className="p-4" data-testid={`health-adapter-${adapter.name}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="truncate text-sm font-medium" title={ADAPTER_LABELS[adapter.name]}>
                  {ADAPTER_LABELS[adapter.name]}
                </div>
                <div className="mt-1 text-xs uppercase text-muted-foreground">{adapter.name}</div>
              </div>
              <Badge
                variant={statusBadgeVariant(adapter.status)}
                data-testid={`health-adapter-${adapter.name}-badge`}
              >
                <span className="mr-1 inline-flex">{statusIcon(adapter.status)}</span>
                {adapter.status}
              </Badge>
            </div>
            <dl className="mt-4 space-y-1 text-xs">
              <div className="flex justify-between gap-2">
                <dt className="text-muted-foreground">Latência</dt>
                <dd
                  className="font-medium"
                  data-testid={`health-adapter-${adapter.name}-latency`}
                >
                  {formatLatency(adapter.lastLatencyMs)}
                </dd>
              </div>
              <div className="flex justify-between gap-2">
                <dt className="text-muted-foreground">Último check</dt>
                <dd className="font-medium">{formatRelative(adapter.lastCheckAt)}</dd>
              </div>
              {adapter.consecutiveFailures > 0 ? (
                <div className="flex justify-between gap-2">
                  <dt className="text-muted-foreground">Falhas consecutivas</dt>
                  <dd
                    className="font-medium text-destructive"
                    data-testid={`health-adapter-${adapter.name}-failures`}
                  >
                    {adapter.consecutiveFailures}
                  </dd>
                </div>
              ) : null}
            </dl>
            {adapter.lastError !== null && adapter.lastError !== '' ? (
              <p
                className="mt-3 line-clamp-3 text-xs text-destructive"
                data-testid={`health-adapter-${adapter.name}-error`}
              >
                {adapter.lastError}
              </p>
            ) : null}
          </Card>
        ))}
        {data.adapters.length === 0 ? (
          <Card className="col-span-full p-4" data-testid="health-empty">
            <p className="text-sm text-muted-foreground">
              HealthMonitor ainda não foi inicializado. Aguardando primeiro check…
            </p>
          </Card>
        ) : null}
      </div>
    </div>
  )
}

export default HealthPage
